import { Injectable } from '@angular/core';
import { BehaviorSubject, throwError } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { ChargeService } from './charge.service';
import { catchError, map } from 'rxjs/operators';
import { PaymentMethodModel } from '../models/payment-method.model';
import { TenantService } from './tenant.service';
import { UtilsService } from './utils.service';
import { PaymentOptionModel } from '../models/payment-option.model';
import { ChargeModel, CurrencyModel } from '../models/charge.model';
import { PaymentModel } from '../models/payment.model';
import { TransactionStatusModel } from '../models/transaction-status.models';
import { ClaimRefundModel } from '../models/claim-refund.model';
import { LocaleService } from './locale.service';
import { KYCModel } from '../models/kyc.model';
import { v4 as uuidv4 } from 'uuid';
import { PaymentNetworkModel } from '../models/payment-network.model';
import { paymentSteps, StepService } from './step.service';

export enum paymentStatuses {
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  OVERPAID = 'OVERPAID',
  UNDERPAID = 'UNDERPAID',
  EXPIRED = 'EXPIRED',
  TERMINATED = 'TERMINATED',
  FAILED = 'FAILED'
}

export enum paymentNavigationItems {
  SCAN = 'SCAN',
  MANUAL = 'MANUAL'
}

export interface ITransactionStatus {
  transaction_id: string;
  status: paymentStatuses;
  order_number: string;
}

export interface ITransactionDetails {
  qr_data: string;
  crypto_amount: string;
  crypto_address: string;
  created_at: number;
  expires_at: number;
  payment_code: string;
  payment_method: PaymentMethodModel;
  payment_option: PaymentOptionModel;
  payment_network: PaymentNetworkModel;
  diff_amount?: string;
  diff_crypto_amount?: string;
  transaction_id?: string;
  payment_flow_type?: string;
  payment_processor?: string;
  currency?: CurrencyModel;
  amount?: number;
  claim_processed: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  payment$$: BehaviorSubject<PaymentModel> = new BehaviorSubject<PaymentModel>(new PaymentModel());
  transactionStatus$$: BehaviorSubject<TransactionStatusModel> = new BehaviorSubject<TransactionStatusModel>(
    new TransactionStatusModel()
  );
  selectedPaymentMethod$$: BehaviorSubject<PaymentMethodModel> = new BehaviorSubject<PaymentMethodModel>(
    new PaymentMethodModel()
  );
  selectedPaymentOption$$: BehaviorSubject<PaymentOptionModel> = new BehaviorSubject<PaymentOptionModel>(
    new PaymentOptionModel()
  );
  selectedPaymentNetwork$$: BehaviorSubject<PaymentNetworkModel> = new BehaviorSubject<PaymentNetworkModel>(
    new PaymentNetworkModel()
  );
  claimRefundForm$$: BehaviorSubject<ClaimRefundModel> = new BehaviorSubject<ClaimRefundModel>(new ClaimRefundModel());
  deepLinks$$: BehaviorSubject<any> = new BehaviorSubject<any>({});
  paymentStatus$$: BehaviorSubject<string> = new BehaviorSubject<string>(paymentStatuses.IN_PROGRESS);
  transactionStatuses$$: BehaviorSubject<ITransactionStatus[]> = new BehaviorSubject<ITransactionStatus[]>([]);
  paymentInProgress$$: BehaviorSubject<ITransactionDetails | null> = new BehaviorSubject<ITransactionDetails | null>(
    null
  );
  paymentFullTime$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  paymentTimeLeft$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  paymentProgressTime$$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  paymentProgressPercentage$$: BehaviorSubject<number> = new BehaviorSubject<number>(100);
  completedPaymentStatuses: string[] = [
    paymentStatuses.SUCCESS,
    paymentStatuses.EXPIRED,
    paymentStatuses.OVERPAID,
    paymentStatuses.UNDERPAID
  ];
  transactionInterval: any;
  kyc$$: BehaviorSubject<KYCModel> = new BehaviorSubject<KYCModel>(new KYCModel());

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private chargeService: ChargeService,
    private tenantService: TenantService,
    private utilsService: UtilsService,
    private localeService: LocaleService,
    private stepService: StepService
  ) {}

  generatePayment(
    paymentMethod: PaymentMethodModel,
    paymentOption: PaymentOptionModel,
    paymentNetwork: PaymentNetworkModel
  ) {
    let paymentBody: any = {
      payment_method: paymentMethod.type,
      amount: this.chargeService.charge$$.value.amount,
      payment_details: this.getPaymentDetails(),
      charge_id: this.chargeService.charge$$.value.charge_id
    };

    if (paymentOption && paymentOption.code) {
      paymentBody = {
        ...paymentBody,
        payment_option: paymentOption.code
      };
    }

    if (paymentNetwork && paymentNetwork.network_type) {
      paymentBody = {
        ...paymentBody,
        payment_network: paymentNetwork.network_type
      };
    }

    if (paymentOption && paymentOption.code && paymentOption.kyc_required) {
      paymentBody = {
        ...paymentBody,
        kyc: this.kyc$$.value
      };
    }

    return this.http
      .post(`${this.tenantService.API_ENDPOINT}/payments/generate/`, paymentBody, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.chargeService.charge$$.value.access_token}`
        })
      })
      .pipe(
        map((data: any) => {
          if (data.deep_links) {
            this.deepLinks$$.next(data.deep_links);
          }

          return data;
        }),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  checkTransactionStatus(transactionId = '') {
    transactionId = transactionId ?? this.payment$$.value.transaction_id;

    return this.http
      .get(`${this.tenantService.API_ENDPOINT}/v2/transactions/${transactionId}/status/`, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.chargeService.charge$$.value.access_token}`
        })
      })
      .pipe(
        map((data: any) => {
          return data;
        }),
        catchError(() => {
          return throwError(() => 'Check transaction status failed!');
        })
      );
  }

  /** Get transaction details */
  getTransactionDetails(transactionId: string) {
    return this.http
      .get(`${this.tenantService.API_ENDPOINT}/transactions/${transactionId}`, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.chargeService.charge$$.value.access_token}`
        })
      })
      .pipe(
        map((data: any) => {
          return data;
        }),
        catchError(() => {
          return throwError(() => 'Check transaction status failed!');
        })
      );
  }

  /** Transactions status by Charge ID - retrieve the transaction status using charge ID from the generated charge request */
  getTransactionStatusByCharge() {
    return this.http
      .get(
        `${this.tenantService.API_ENDPOINT}/transactions/${this.chargeService.charge$$.value.charge_id}/status/all/`,
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.chargeService.charge$$.value.access_token}`
          })
        }
      )
      .pipe(
        map((data: any) => {
          return data;
        }),
        catchError(() => {
          return throwError(() => 'Payment terminate failed!');
        })
      );
  }

  /** Navigate the user to a proper screen based on the transaction status */
  navigateTransactionStatus() {
    this.getTransactionStatusByCharge().subscribe({
      next: (data: ITransactionStatus[]) => {
        this.transactionStatuses$$.next(data);
        if (!data || !data.length) {
          this.stepService.toStep(paymentSteps.PAYMENT_METHOD_SELECT);
          return;
        }

        /** Check last payment */
        const lastTransaction = data[0];
        this.paymentStatus$$.next(lastTransaction.status);

        /** In case of terminated transaction redirect or reset the charge based on the is_persistent property in charge API */
        const terminatedTransaction = lastTransaction.status === paymentStatuses.TERMINATED;

        if (terminatedTransaction && this.chargeService.charge$$.value.is_persistent) {
          this.stepService.toStep(paymentSteps.PAYMENT_METHOD_SELECT);
          return;
        }

        if (terminatedTransaction && !this.chargeService.charge$$.value.is_persistent) {
          const callbackEndpoint = this.utilsService.modifyUrl(
            this.chargeService.charge$$.value.callback_endpoint,
            `payment_status=${paymentStatuses.TERMINATED}`
          );
          this.utilsService.skipUnloadConfirm$$.next(true);
          window.location.href = callbackEndpoint;
          return;
        }

        /** If there is a payment in progress inside the transaction statues, generate a qr code and redirect the user back to the payment processor */
        const paymentInProgress = [
          paymentStatuses.IN_PROGRESS,
          paymentStatuses.UNDERPAID,
          paymentStatuses.OVERPAID,
          paymentStatuses.EXPIRED,
          paymentStatuses.FAILED,
          paymentStatuses.TERMINATED
        ].includes(lastTransaction.status);

        if (paymentInProgress) {
          this.getTransactionDetails(lastTransaction.transaction_id).subscribe({
            next: (data: ITransactionDetails) => {
              this.paymentInProgress$$.next(data);
              this.payment$$.next({
                qr_data: data.diff_amount ? (parseFloat(data.diff_amount) < 0 ? '' : data.qr_data) : data.qr_data,
                payment_methods: [],
                payment_method: data.payment_method,
                crypto_amount: data.crypto_amount,
                crypto_address: data.crypto_address,
                payment_code: data.payment_code,
                transaction_id: lastTransaction.transaction_id,
                redirect_url: '',
                created_at: data.created_at,
                expires_at: data.expires_at
              });
              this.selectedPaymentMethod$$.next(data.payment_method);
              this.selectedPaymentOption$$.next(data.payment_option);
              this.selectedPaymentNetwork$$.next(data.payment_network);

              if (data.claim_processed) {
                this.stepService.toStep(paymentSteps.PAYMENT_STATIC_SUCCESS);
                return;
              }

              this.stepService.toStep(paymentSteps.PAYMENT_PROCESSOR);
            },
            error: () => {
              if (this.chargeService.charge$$.value.created_at) {
                this.stepService.toStep(paymentSteps.PAYMENT_METHOD_SELECT);
                return;
              }

              this.stepService.toStep(paymentSteps.PAYMENT_STATIC_ERROR);
            }
          });
          return;
        }

        /** If the payment was already successful redirect him to a success page */
        const successStatus = lastTransaction.status === paymentStatuses.SUCCESS;

        if (!successStatus) {
          this.paymentStatus$$.next(paymentStatuses.FAILED);
          this.stepService.toStep(paymentSteps.PAYMENT_STATIC_ERROR);
          return;
        }

        this.stepService.toStep(paymentSteps.PAYMENT_STATIC_SUCCESS);
        this.paymentStatus$$.next(paymentStatuses.SUCCESS);
      },
      error: () => {
        /** redirect the user to an error page */
        this.paymentStatus$$.next(paymentStatuses.FAILED);
        this.stepService.toStep(paymentSteps.PAYMENT_STATIC_ERROR);
      }
    });
  }

  terminatePayment() {
    return this.http
      .post(
        `${this.tenantService.API_ENDPOINT}/payments/terminate/`,
        {
          payment_code: this.payment$$.value.payment_code === '' ? uuidv4() : this.payment$$.value.payment_code,
          charge_id: this.chargeService.charge$$.value.charge_id
        },
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.chargeService.charge$$.value.access_token}`
          })
        }
      )
      .pipe(
        map((data: any) => {
          return data;
        }),
        catchError(() => {
          return throwError(() => 'Payment terminate failed!');
        })
      );
  }

  claimRefund(transactionId: string, formData: object) {
    return this.http
      .post(
        `${this.tenantService.API_ENDPOINT}/payments/claim-refund/`,
        {
          transaction_id: transactionId,
          ...formData
        },
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept-Language': this.localeService.currentLocale,
            Authorization: `Bearer ${this.chargeService.charge$$.value.access_token}`
          })
        }
      )
      .pipe(
        map((data: any) => {
          return data;
        }),
        catchError(() => {
          return throwError(() => 'Claim refund failed!');
        })
      );
  }

  getPaymentDetails() {
    const charge: ChargeModel = this.chargeService.charge$$.value;
    let paymentDetails = {};

    if ('items' in charge) {
      paymentDetails = {
        ...paymentDetails,
        items: charge.items
      };
    }

    return paymentDetails;
  }

  getQRCodeIcon() {
    const excludeSections = ['CRYPTO_CURRENCY'];
    const selectedPaymentOptionSection = this.selectedPaymentOption$$.value.section;
    if (excludeSections.includes(selectedPaymentOptionSection)) {
      return '';
    }
    if (selectedPaymentOptionSection === 'WALLET') {
      return `assets/icons/payment-options/${this.selectedPaymentOption$$.value.code.toLowerCase()}-wallet-icon.svg`;
    }
    return `assets/icons/payment-methods/${this.selectedPaymentMethod$$.value.type.toLowerCase()}-icon.svg`;
  }

  resetPayment(bypassProgressReset = false) {
    this.payment$$.next(new PaymentModel());
    this.paymentStatus$$.next(paymentStatuses.IN_PROGRESS);
    this.paymentFullTime$$.next(0);
    this.paymentTimeLeft$$.next(0);
    this.paymentProgressTime$$.next('');
    this.paymentProgressPercentage$$.next(100);
    this.deepLinks$$.next({});
    this.terminateTransactionStatusInterval();

    if (bypassProgressReset) {
      return;
    }
    this.paymentInProgress$$.next(null);
  }

  startTransactionStatusInterval(includeProgressTime = false, transactionId = '') {
    this.transactionInterval = setInterval(() => {
      if (
        this.paymentStatus$$.value === paymentStatuses.UNDERPAID ||
        this.paymentStatus$$.value === paymentStatuses.OVERPAID ||
        (this.paymentTimeLeft$$.value > 0 && this.paymentStatus$$.value === paymentStatuses.IN_PROGRESS)
      ) {
        this.paymentTimeLeft$$.next(this.paymentTimeLeft$$.value - 1);
        if (includeProgressTime) {
          this.paymentProgressTime$$.next(this.utilsService.parseTime(this.paymentTimeLeft$$.value));
          this.paymentProgressPercentage$$.next(this.calculateProgressPercent());
        }
        this.checkTransactionStatus(transactionId).subscribe({
          next: (data: TransactionStatusModel) => {
            this.transactionStatus$$.next(data);

            if (data.status === paymentStatuses.IN_PROGRESS) {
              return;
            }

            if (this.completedPaymentStatuses.includes(data.status)) {
              this.paymentStatus$$.next(data.status);
              this.terminateTransactionStatusInterval();
              return;
            }

            if (data.status === paymentStatuses.TERMINATED) {
              this.paymentStatus$$.next(paymentStatuses.TERMINATED);
              this.terminateTransactionStatusInterval();
              return;
            }

            this.paymentStatus$$.next(paymentStatuses.FAILED);
            this.terminateTransactionStatusInterval();
          },
          /*TODO: Add some failsafe on error*/
          error: (error) => {
            /*this.paymentStatus$$.next(paymentStatuses.FAILED);
            this.terminateTransactionStatusInterval();*/
            console.error('error fetching status: ', error);
          }
        });
      } else {
        this.terminateTransactionStatusInterval();
      }
    }, 1000);
  }

  terminateTransactionStatusInterval() {
    clearInterval(this.transactionInterval);
  }

  calculateProgressPercent() {
    return (this.paymentTimeLeft$$.value * 100) / this.paymentFullTime$$.value;
  }

  checkKYC(): boolean {
    const kyc = this.kyc$$.value;
    let kycValid = false;
    Object.entries(kyc).forEach(([_, attributeValue]) => {
      kycValid = attributeValue !== '';
    });
    return kycValid;
  }
}
